import { useEffect, useState } from "react";

import { listGrantOpportunities, saveGrantFromCatalog, saveGrantToWatchlist } from "@/api/grant_opportunities";
import type { DiscoverGrant } from "@/components/grant_suite/grantDiscoverUi";

type UseGrantDiscoverTrackingOptions = {
  municipalityId?: number;
  canUseGrantPipeline: boolean;
  onSavedToWatchlist?: () => void;
};

export function useGrantDiscoverTracking({
  municipalityId,
  canUseGrantPipeline,
  onSavedToWatchlist,
}: UseGrantDiscoverTrackingOptions) {
  const [watchlistSaving, setWatchlistSaving] = useState<Record<number, boolean>>({});
  const [pursuingSaving, setPursuingSaving] = useState<Record<number, boolean>>({});
  const [savedGrantIds, setSavedGrantIds] = useState<Set<number>>(new Set());
  const [pursuingGrantIds, setPursuingGrantIds] = useState<Set<number>>(new Set());
  // ENG-1690: catalog grant_id -> tracked opportunity id, so an already-pursued
  // discover card can deep-link to that grant in the tracker.
  const [pursuingOpportunityByGrantId, setPursuingOpportunityByGrantId] = useState<
    Map<number, number>
  >(new Map());
  const [watchlistError, setWatchlistError] = useState<string | null>(null);

  useEffect(() => {
    if (!canUseGrantPipeline || !municipalityId) {
      setSavedGrantIds(new Set());
      setPursuingGrantIds(new Set());
      setPursuingOpportunityByGrantId(new Map());
      return;
    }

    let cancelled = false;
    listGrantOpportunities(municipalityId)
      .then((opportunities) => {
        if (cancelled) return;
        const saved = new Set<number>();
        const pursuing = new Set<number>();
        const pursuingOpps = new Map<number, number>();
        opportunities.forEach((opp) => {
          if (opp.grant_id == null) return;
          if (opp.watching) {
            saved.add(opp.grant_id);
          } else {
            pursuing.add(opp.grant_id);
            pursuingOpps.set(opp.grant_id, opp.id);
          }
        });
        setSavedGrantIds(saved);
        setPursuingGrantIds(pursuing);
        setPursuingOpportunityByGrantId(pursuingOpps);
      })
      .catch(() => {
        if (!cancelled) {
          setSavedGrantIds(new Set());
          setPursuingGrantIds(new Set());
          setPursuingOpportunityByGrantId(new Map());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canUseGrantPipeline, municipalityId]);

  const handleSaveToWatchlist = async (grant: DiscoverGrant) => {
    if (!canUseGrantPipeline || !municipalityId) return;

    setWatchlistError(null);
    setWatchlistSaving((prev) => ({ ...prev, [grant.id]: true }));
    try {
      await saveGrantToWatchlist(municipalityId, grant.id);
      setSavedGrantIds((prev) => new Set(prev).add(grant.id));
      setPursuingGrantIds((prev) => {
        const next = new Set(prev);
        next.delete(grant.id);
        return next;
      });
      setPursuingOpportunityByGrantId((prev) => {
        const next = new Map(prev);
        next.delete(grant.id);
        return next;
      });
      onSavedToWatchlist?.();
    } catch (err) {
      setWatchlistError(err instanceof Error ? err.message : "Could not save to watchlist");
    } finally {
      setWatchlistSaving((prev) => ({ ...prev, [grant.id]: false }));
    }
  };

  const handleStartPursuing = async (grant: DiscoverGrant) => {
    if (!canUseGrantPipeline || !municipalityId) return;

    setWatchlistError(null);
    setPursuingSaving((prev) => ({ ...prev, [grant.id]: true }));
    try {
      await saveGrantFromCatalog(municipalityId, grant.id, { watching: false });
      // Keep the just-pursued confirmation ("In pursuit"); the deep-link
      // ("Open in tracker") applies to grants already in pursuit on load (ENG-1690),
      // so pursuingOpportunityByGrantId is only populated from the initial fetch.
      setPursuingGrantIds((prev) => new Set(prev).add(grant.id));
      setSavedGrantIds((prev) => {
        const next = new Set(prev);
        next.delete(grant.id);
        return next;
      });
      onSavedToWatchlist?.();
    } catch (err) {
      setWatchlistError(err instanceof Error ? err.message : "Could not start pursuing this grant");
    } finally {
      setPursuingSaving((prev) => ({ ...prev, [grant.id]: false }));
    }
  };

  return {
    watchlistSaving,
    pursuingSaving,
    savedGrantIds,
    pursuingGrantIds,
    pursuingOpportunityByGrantId,
    watchlistError,
    handleSaveToWatchlist,
    handleStartPursuing,
  };
}
